import { useNavigate, useParams } from 'react-router-dom';
import './update-instructor.css';
import { useUI } from '../../UIContext';
import { useEffect, useState } from 'react';
import { get, getBlob, post, put, remove } from '../../services/api';
import { handleErr } from '../../utils/functions';
import CloseButton from '../../components/CloseButton';
import PhotoInput from '../../components/PhotoInput';
import Input from '../../components/Input';
import TextArea from '../../components/TextArea';
import ConfirmationModal from '../../components/ConfirmationModal';

function UpdateInstructor() {
    const { id } = useParams();

    const navigate = useNavigate();

    const { setMessage, setLoading } = useUI();
    const [instructor, setInstructor] = useState({});
    const [photo, setPhoto] = useState(undefined);
    const [submitAttempt, setSubmitAttempt] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [hasSavedProcess, setHasSavedProcess] = useState(false);

    function isNameValid() {
        return !!instructor.name && instructor.name.length >= 2 && instructor.name.length <= 56;
    }

    function isLastNameValid() {
        return !!instructor.lastName && instructor.lastName.length >= 2 && instructor.lastName.length <= 200;
    }

    function isDescriptionValid() {
        return !instructor.description || instructor.description.length <= 400;
    }

    useEffect(() => {
        async function getInstructorPhoto() {
            await getBlob(`/instructor/${id}/photo`, true, setMessage,
                (response) => {
                    setPhoto(response.data);
                },
                (err) => {
                    handleErr(setMessage, `Falha ao carregar a foto do professor: ${err}`);
                },
                () => {
                    setLoading(false);
                }
            );
        }

        async function getInstructorData() {
            setLoading(true);

            await get(`instructor/${id}`, true, setMessage,
                async (response) => {
                    const photo = {
                        path: response.data.photoPath,
                        changed: false
                    };

                    setInstructor({ 
                        ...response.data,
                        photo: photo
                    });

                    if (!photo.path) {
                        setLoading(false);
                        return;
                    }

                    await getInstructorPhoto();
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar o professor: ${err}`);
                }
            )
        }

        getInstructorData();
    }, [id, setInstructor, setLoading, setMessage]);

    function showSuccess() {
        setHasSavedProcess(true);
        setMessage({ type: 'success', text: 'O professor foi modificado.' });
    }

    async function addPhoto() {
        const formData = new FormData();
        formData.append('photo', photo)
        await post(`/instructor/${id}/photo`, formData, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados do professor: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function removePhoto() {
        await remove(`/instructor/${id}/photo`, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados do professor: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function onClickSave(e) {

        function isSaveValid() {
            setSubmitAttempt(true);

            return !!instructor.name && !!instructor.lastName;
        }

        e.preventDefault();

        if (!isSaveValid())
            return;

        setLoading(true);

        const data = {
            name: instructor.name,
            lastName: instructor.lastName,
            description: instructor.description || null,
        };

        await put(`instructor/${instructor.id}`, data, true, setMessage,
            async () => {
                if (!!instructor.photo.changed)
                    await addPhoto();
                else if (!!instructor.photo.path && !photo)
                    await removePhoto();
                else
                    showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Não foi possível atualizar o professor: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    function onChangePhotoUpload(file) {
        setPhoto(file);
        setInstructor({
            ...instructor,
            photo: {
                ...instructor.photo,
                changed: true
            }
        });
    }

    function onRemovePhoto() {
        setPhoto(undefined);
        setInstructor({
            ...instructor,
            photo: {
                ...instructor.photo,
                changed: false
            }
        });
    }

    function onClickRemove(e) {
        e.preventDefault();
        
        setShowDeleteConfirmation(true);
    }

    async function removeInstructor() {
        setShowDeleteConfirmation(false);
        setLoading(true);

        await remove(`instructor/${instructor.id}`, true, setMessage,
            () => {
                navigate('/instructors');
                setMessage({ type: 'success', text: 'O professor foi removido.' });
            },
            (err) => {
                handleErr(setMessage, `Não foi possível remover o professor: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    return (
        <>
            <form className='update-instructor-form'>
                <header className='container-header'>
                    <h1>Editar Instrutor</h1>

                    <CloseButton
                        onClick={() => navigate('/instructors')}
                        shouldConfirm={!hasSavedProcess}
                    />
                </header>

                <div className='instructor-photo-container'>
                    <PhotoInput
                        photo={photo}
                        onChange={onChangePhotoUpload}
                        onRemove={onRemovePhoto}
                    />
                </div>

                <Input
                    className='commom-input glass-effect-15'
                    type='text'
                    value={instructor.name}
                    onChange={(e) => setInstructor({
                        ...instructor,
                        name: e.target.value
                    })}
                    onValidate={isNameValid}
                    placeholder='Nome'
                    shouldValidate={submitAttempt}
                    maxLength={56}
                    warningMsg='O nome deve conter ao menos 2 caracteres.'
                />

                <Input
                    className='commom-input glass-effect-15'
                    type='text'
                    value={instructor.lastName}
                    onChange={(e) => setInstructor({
                        ...instructor,
                        lastName: e.target.value
                    })}
                    onValidate={isLastNameValid}
                    placeholder='Sobrenome'
                    shouldValidate={submitAttempt}
                    maxLength={200}
                    warningMsg='O sobrenome deve conter ao menos 2 caracteres.'
                />

                <TextArea
                    rows={5}
                    className='commom-input glass-effect-15'
                    value={instructor.description || ''}
                    onChange={(e) => setInstructor({
                        ...instructor,
                        description: e.target.value
                    })}
                    onValidate={isDescriptionValid}
                    placeholder='Descrição (opcional)'
                    shouldValidate={submitAttempt}
                    maxLength={400}
                    warningMsg='A descrição deve conter no máximo 400 caracteres.'
                />

                <div className='update-instructor-buttons'>
                    <button
                        type='submit'
                        onClick={onClickRemove}
                        className='remove-instructor red-button'>
                        Excluir
                    </button>

                    <button
                        type='submit'
                        onClick={onClickSave}
                        className='blue-button zoom-effect'>
                        Salvar
                    </button>
                </div>
            </form>

            <ConfirmationModal
                show={showDeleteConfirmation}
                title='Excluir o professor'
                description='Atenção! Esta opção é irreversível. Você realmente deseja excluir o professor?'
                onConfirm={() => removeInstructor()}
                onClose={() => setShowDeleteConfirmation(false)}
            />
        </>
    );
}

export default UpdateInstructor;